import styles from './index.module.css'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import {
  discardBriefChanges,
  getBrief,
  getBriefsState,
  loadBrief,
  publishBrief,
  saveBrief,
  updateBrief,
} from '@disco/state-management/reducers/entities/briefs'
import { Brief } from '@disco/types/types/brief'
import {
  formatScheduledCampaignData,
  getTimeDateToScheduleEmail,
} from '@disco/utils/emailCampaigns'
import { pushUINotification } from '@disco/utils/notifications'
import { checkDateIsInPast, checkTimeIsInPast } from '@disco/utils/time-utils'

import { ScheduledValue } from 'types/campaign'

import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import {
  AccessibleIcon,
  Button as DsButton,
  Dialog,
  DiscoCallout,
  DropdownMenu,
  Flex,
  IconButton,
  SplitButton,
  TextField,
} from 'components/ds'
import { EmailSchedulerModal } from 'components/modals/email-scheduler'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import HTMLEditor from 'components/ui/html-editor'
import Icon from 'components/ui/icon'
import {
  EllipsisVerticalIcon,
  ExternalLinkIcon,
} from 'components/ui/icon/svg-icons'
import Label from 'components/ui/label'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Spinner from 'components/ui/spinner'
import BriefActionMenu from 'features/briefs/components/brief-action-menu'

import { useDispatch, useSelector } from 'hooks/use-redux'
import mixpanel from '@disco/api/mixpanel'

const rowSizes = {
  8_4: [8, 4],
  6_6: [6, 6],
  12: [12],
}

const PublishConfirmationModal = ({
  onClose,
  brief,
}: {
  onClose: () => void
  brief: Brief
}) => {
  const navigate = useNavigate()
  return (
    <Dialog.Root defaultOpen>
      <Dialog.Content maxWidth="600px">
        <Dialog.Title>This brief is now live!</Dialog.Title>
        <Dialog.Description>
          Share with recipients to get started. Submissions for this brief will
          appear in the briefs tab.
        </Dialog.Description>
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <DsButton variant="soft" color="gray" onClick={onClose}>
              Not now
            </DsButton>
          </Dialog.Close>
          <Dialog.Close
            onClick={async () => {
              navigate(`/brief-editor-v2/${brief.id}/share`)
            }}
          >
            <DsButton>Share</DsButton>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export const BriefEditorV2Page = () => {
  const params = useParams<{ briefId: string }>()
  const dispatch = useDispatch()
  const [publishing, setPublishing] = useState(false)

  useEffect(() => {
    dispatch(loadBrief(parseInt(params.briefId)))
  }, [params.briefId, dispatch])

  const briefsState = useSelector(getBriefsState)
  const brief = useSelector(state =>
    getBrief(state, { briefId: parseInt(params.briefId) }),
  )

  const [isDeadlinePickerOpen, setIsDeadlinePickerOpen] = useState(false)
  const [showPublishConfirmationModal, setShowPublishConfirmationModal] =
    useState(false)

  const campaignSchedule = formatScheduledCampaignData({
    scheduled_at: brief?.deadline,
    scheduled_at_timezone: brief?.deadline_timezone,
    status: 'draft',
  })

  const canPublish =
    brief?.name &&
    Boolean(brief?.description?.trim()) &&
    Boolean(brief?.description_html?.trim()) &&
    brief?.description_html?.trim() !== '<p><br></p>' &&
    Boolean(brief?.deadline) &&
    Boolean(brief?.deadline_timezone)

  const onDescriptionChange = useCallback(
    (description, descriptionHtml) => {
      if (brief?.description_html !== descriptionHtml) {
        dispatch(
          updateBrief({
            ...brief,
            description: JSON.stringify(description),
            description_html: descriptionHtml,
          }),
        )
      }
    },
    [brief, dispatch],
  )

  const initialContent = useMemo(() => {
    if (brief?.description && briefsState.loaded) {
      try {
        return JSON.parse(brief?.description)
      } catch (error) {
        return null
      }
    }
  }, [brief?.description, briefsState.loaded])

  const handleSetDeadline = ({
    date,
    time,
    timeZone,
  }: {
    date: string
    time: ScheduledValue
    timeZone: ScheduledValue
  }) => {
    const deadlineToScheduleEmail = getTimeDateToScheduleEmail(
      date as any,
      time.value,
      timeZone.value,
    )

    mixpanel.sendPureEvent('Brief deadline set', {
      Context: 'Briefs',
      brief_id: brief.id,
    })

    dispatch(
      updateBrief({
        ...brief,
        deadline: deadlineToScheduleEmail,
        deadline_timezone: timeZone.value,
      }),
    )
  }

  const handlePublish = async () => {
    if (!canPublish) return

    // Validate that deadline is in the future
    if (brief?.deadline && brief?.deadline_timezone) {
      const isDeadlineInPast = checkDateIsInPast(
        brief.deadline,
        brief.deadline_timezone,
      )

      const isTimeInPast = checkTimeIsInPast(
        brief.deadline,
        new Date().toISOString(),
      )

      if (isDeadlineInPast || isTimeInPast) {
        pushUINotification({
          message: 'Brief deadline must be in the future',
          level: 'error',
        })
        return
      }
    }

    setPublishing(true)
    if (brief?.dirty) {
      await dispatch(saveBrief(brief))
    }
    try {
      mixpanel.sendPureEvent('Brief publish attempted', {
        Context: 'Briefs',
        brief_id: brief.id,
      })
      await dispatch(publishBrief(brief.id)).unwrap()
      setShowPublishConfirmationModal(true)
    } catch (_) {
    } finally {
      setPublishing(false)
    }
  }

  if (!brief || !briefsState.loaded) return <Spinner />

  const backLink = brief?.project
    ? { content: 'Briefs', link: `/briefs/projects/${brief.project.id}` }
    : { content: 'Briefs', link: '/briefs' }

  return (
    <div className="Page">
      <div className="Page_Content">
        <DocumentHeadTags title={`Edit Brief | DISCO`} />
        <PageHeader
          breadcrumbs={backLink}
          title={brief.name || 'Untitled Brief'}
          actions={
            <ButtonContainer>
              {brief?.is_dirty &&
              !brief?.dirty &&
              // discard will work only if the brief is published
              brief?.publishing_status === 'published' ? (
                <Button
                  appearance="inline-link"
                  caption="Discard draft"
                  disabled={!brief?.is_dirty}
                  progressIndicator={brief?.discarding}
                  progressCaption="Discarding draft"
                  onClick={() => {
                    mixpanel.sendPureEvent('Brief draft discarded', {
                      Context: 'Briefs',
                      brief_id: brief.id,
                    })
                    dispatch(discardBriefChanges(brief.id))
                  }}
                />
              ) : (
                <Button
                  appearance="inline-link"
                  caption="Save draft"
                  disabled={!brief?.dirty}
                  progressIndicator={brief?.saving && !publishing}
                  progressCaption="Saving draft"
                  onClick={() => {
                    mixpanel.sendPureEvent('Brief draft saved', {
                      Context: 'Briefs',
                      brief_id: brief.id,
                    })
                    dispatch(saveBrief(brief))
                  }}
                />
              )}

              <SplitButton
                caption="View brief"
                iconNode={<ExternalLinkIcon size={16} />}
                variant="soft"
                color="gray"
                onClick={() => {
                  mixpanel.sendPureEvent('Brief preview opened', {
                    Context: 'Briefs',
                    brief_id: brief.id,
                    is_draft: brief.is_dirty,
                  })
                  if (brief?.latest_version_id) {
                    window.open(
                      `/brief-editor/${params.briefId}/${brief.inbox}/preview?version=${brief?.latest_version_id}`,
                      '_blank',
                    )
                  } else {
                    window.open(
                      `/brief-editor/${params.briefId}/${brief.inbox}/preview`,
                      '_blank',
                    )
                  }
                }}
                dropdownItems={[
                  {
                    label: 'Preview draft',
                    onSelect: async () => {
                      if (brief?.dirty) {
                        await dispatch(saveBrief(brief))
                      }
                      mixpanel.sendPureEvent('Brief preview opened', {
                        Context: 'Briefs',
                        brief_id: brief.id,
                        is_draft: brief.is_dirty,
                      })
                      window.open(
                        `/brief-editor/${params.briefId}/${brief.inbox}/preview`,
                        '_blank',
                      )
                    },
                    disabled: !brief?.is_dirty,
                  },
                ]}
              />
              <Button
                appearance="primary"
                caption="Publish"
                disabled={!canPublish}
                progressIndicator={publishing}
                progressCaption="Publishing"
                onClick={handlePublish}
              />
              <BriefActionMenu brief={brief} editMode />
            </ButtonContainer>
          }
        />
        <PageContainer>
          <PageContainer.Section>
            <div className={styles.singlePageEditor}>
              {brief?.publishing_status === 'published' &&
                brief?.submission_status === 'open' && (
                  <>
                    <DiscoCallout type="info" mb="5">
                      This brief is now live, any published updates will be
                      visible to all recipients.
                    </DiscoCallout>
                  </>
                )}
              <Row columnsSize={rowSizes[8_4]}>
                <div>
                  <Label caption="Brief name" />
                  <TextField.Root
                    size="2"
                    type="text"
                    autoComplete="off"
                    value={brief?.name || ''}
                    onChange={event => {
                      dispatch(
                        updateBrief({ ...brief, name: event.target.value }),
                      )
                    }}
                  />
                </div>
                <div>
                  <Label caption="Brief deadline" />
                  <TextField.Root
                    size="2"
                    id="deadline"
                    type="text"
                    value={
                      brief?.deadline
                        ? `${campaignSchedule?.date.label}, ${campaignSchedule?.time.label} ${campaignSchedule?.timeZone.value}`
                        : ''
                    }
                    onClick={() => {
                      setIsDeadlinePickerOpen(true)
                    }}
                    onChange={() => {
                      setIsDeadlinePickerOpen(true)
                    }}
                  >
                    {brief?.deadline && (
                      <TextField.Slot
                        side="right"
                        tabIndex={-1}
                        style={{ cursor: 'pointer' }}
                      >
                        <IconButton
                          size="1"
                          tabIndex={-1}
                          variant="ghost"
                          color="gray"
                          onClick={() => {
                            dispatch(
                              updateBrief({
                                ...brief,
                                deadline: null,
                                deadline_timezone: null,
                              }),
                            )
                          }}
                        >
                          <Icon type="close" width={16} height={16} />
                        </IconButton>
                      </TextField.Slot>
                    )}
                    <TextField.Slot side="right" tabIndex={-1}>
                      <IconButton
                        size="1"
                        tabIndex={-1}
                        variant="ghost"
                        color="gray"
                        onClick={() => {
                          setIsDeadlinePickerOpen(true)
                        }}
                      >
                        <Icon type="calendar" width={16} height={16} />
                      </IconButton>
                    </TextField.Slot>
                  </TextField.Root>
                </div>
              </Row>
              <Row columnsSize={rowSizes[12]}>
                <div>
                  <Label caption="Brief description" />
                  <HTMLEditor
                    // key to force re-render when is_dirty changes to force re-render the editor
                    key={`brief-editor-${brief.id}-${brief.is_dirty}`}
                    initialContent={initialContent}
                    contentClassName={styles.editorContent}
                    placeholder="Include key details like usage type, media, term, territory, mood, lyrical themes, reference tracks, and any creative or licensing restrictions."
                    onChange={onDescriptionChange}
                    ds
                  />
                </div>
              </Row>
            </div>
          </PageContainer.Section>
        </PageContainer>

        {/* Modals */}
        {isDeadlinePickerOpen && (
          <EmailSchedulerModal
            onClose={() => setIsDeadlinePickerOpen(false)}
            setScheduleData={handleSetDeadline}
            storedCampaignSchedule={campaignSchedule}
            caption="Brief Deadline"
            description="Pick a date, time and timezone for your submissions deadline. Once the deadline has passed submissions for this brief will be closed."
            primaryButtonCaption="Done"
            showCloseButton={false}
          />
        )}
        {showPublishConfirmationModal && (
          <PublishConfirmationModal
            onClose={() => setShowPublishConfirmationModal(false)}
            brief={brief}
          />
        )}
      </div>
      <Outlet />
    </div>
  )
}
